// apps/web/src/pages/GetStarted.tsx
// The fresh-install experience (native, logged out). Landing → question wizard
// (answers held on device) → plan → create account. This file owns the landing
// and the shell; the wizard lives in OnboardingQuiz. Existing users sign in via
// the bottom sheet without ever touching the questions.
import { useEffect, useState } from "react";
import { Globe } from "lucide-react";
import { useT, useLocale, type Locale } from "../lib/i18n";
import { forceTheme } from "../lib/theme";
import { Button } from "../components/ui/Button";
import { ScanShowcase, INTRO_BG } from "../components/onboarding/ScanShowcase";
import { LanguageSheet } from "../components/LanguageSheet";
import { SignInSheet } from "../components/SignInSheet";
import { OnboardingQuiz } from "../components/onboarding/OnboardingQuiz";

const FLAG: Record<Locale, string> = { en: "🇬🇧", de: "🇩🇪", es: "🇪🇸", fr: "🇫🇷", it: "🇮🇹" };

export function GetStarted({ onAuthed }: { onAuthed: () => void }) {
  const t = useT();
  const { locale } = useLocale();
  const [langOpen, setLangOpen] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);
  const [started, setStarted] = useState(false);

  // The whole Get Started experience (welcome mockup + question wizard) is
  // ALWAYS the white version (owner) — pinned on <html> so portaled sheets
  // are covered too. Unmount restores the device's saved theme.
  useEffect(() => {
    forceTheme("light");
    return () => forceTheme(null);
  }, []);

  if (started) {
    return <OnboardingQuiz onAuthed={onAuthed} onExit={() => setStarted(false)} />;
  }

  return (
    <div className="page-scroll relative min-h-screen-d flex flex-col" style={{ ...INTRO_BG, color: "#101711" }}>
      {/* language chip, top-right, over the safe area */}
      <div className="flex justify-end px-4 pt-safe">
        <button
          type="button"
          onClick={() => setLangOpen(true)}
          className="mt-3 flex items-center gap-2 rounded-full bg-black/5 px-3 py-2 text-sm font-medium press"
          aria-label={t("start.language")}
        >
          <span aria-hidden="true" className="text-base leading-none">{FLAG[locale]}</span>
          <span className="uppercase tracking-wide">{locale}</span>
          <Globe size={15} style={{ color: "#7a877d" }} aria-hidden="true" />
        </button>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-6 py-4">
        <ScanShowcase />
      </div>

      <div className="px-6 pb-safe">
        <div className="mx-auto w-full max-w-sm pb-6">
          <h1 className="mb-6 text-center text-3xl font-extrabold leading-tight tracking-tight">
            {t("start.welcome_headline")}
          </h1>
          <Button fullWidth onClick={() => setStarted(true)}>{t("start.getStarted")}</Button>
          <p className="mt-4 text-center text-sm" style={{ color: "#3d4a40" }}>
            {t("start.haveAccount")}{" "}
            <button type="button" onClick={() => setSignInOpen(true)} className="font-bold underline press">
              {t("start.signIn")}
            </button>
          </p>
        </div>
      </div>

      <LanguageSheet open={langOpen} onClose={() => setLangOpen(false)} />
      <SignInSheet open={signInOpen} onClose={() => setSignInOpen(false)} onAuthed={onAuthed} />
    </div>
  );
}
